import React from "react";
import styled, { keyframes } from "styled-components";
import { Monitor, X, Minus, Lock, MousePointerClick } from "lucide-react";
import socket from "../../../services/socket";

type PreviewBox = { x: number; y: number; width: number; height: number };
type PreviewElement = { index: number; text?: string; tag?: string; box?: PreviewBox };
export type BrowserUsePreviewFrame = {
   projectId?: string;
   nodeIndex?: string;
   processId?: string;
   operation?: string;
   step?: number;
   url?: string;
   title?: string;
   label?: string;
   highlightIndex?: number;
   viewport?: { width: number; height: number };
   elements?: PreviewElement[];
   image?: string;
   done?: boolean;
   waiting?: boolean;
};

export const BROWSER_USE_PREVIEW_EVENT = "dragify-browser-use-preview";

export function pushBrowserUsePreview(frame: BrowserUsePreviewFrame) {
   window.dispatchEvent(new CustomEvent(BROWSER_USE_PREVIEW_EVENT, { detail: frame }));
}

const ACTION_COPY: Record<string, string> = {
   starting: "Agent is opening the browser…",
   navigate: "Opening page",
   view: "Scanning clickable elements",
   click_element: "Clicking",
   input_text: "Typing",
   scroll: "Scrolling",
   screenshot: "Capturing page",
   extract: "Reading page text",
   done: "Finished",
};

const pulse = keyframes`
   0% { transform: translate(-50%, -50%) scale(0.7); opacity: 0.9; }
   70% { transform: translate(-50%, -50%) scale(1.45); opacity: 0; }
   100% { transform: translate(-50%, -50%) scale(1.45); opacity: 0; }
`;

const Panel = styled.div<{ $minimized: boolean }>`
   position: fixed;
   right: 16px;
   top: 64px;
   z-index: 5000;
   width: ${p => p.$minimized ? "280px" : "min(720px, calc(100vw - 28px))"};
   max-height: calc(100vh - 88px);
   display: flex;
   flex-direction: column;
   background: #0b1220;
   color: #e2e8f0;
   border-radius: 16px;
   box-shadow: 0 28px 70px rgba(2, 6, 23, 0.55);
   overflow: hidden;
   border: 1px solid rgba(56, 189, 248, 0.45);
`;

const Chrome = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
   border-bottom: 1px solid rgba(148, 163, 184, 0.18);
`;

const Dots = styled.div`
   display: flex;
   gap: 6px;
   span {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #64748b;
      &:nth-child(1) { background: #f87171; }
      &:nth-child(2) { background: #fbbf24; }
      &:nth-child(3) { background: #34d399; }
   }
`;

const Title = styled.div`
   font-size: 12px;
   font-weight: 700;
   letter-spacing: 0.02em;
   color: #f8fafc;
   display: flex;
   align-items: center;
   gap: 6px;
`;

const UrlBar = styled.div`
   flex: 1;
   min-width: 0;
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 12px;
   background: #020617;
   border: 1px solid rgba(148, 163, 184, 0.18);
   border-radius: 999px;
   padding: 7px 12px;
   color: #cbd5e1;
`;

const UrlText = styled.div`
   min-width: 0;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;

const ActionChip = styled.span`
   flex-shrink: 0;
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   background: #0ea5e9;
   color: #fff;
   border-radius: 999px;
   padding: 4px 8px;
`;

const IconBtn = styled.button`
   border: 0;
   background: transparent;
   color: #94a3b8;
   cursor: pointer;
   width: 28px;
   height: 28px;
   border-radius: 8px;
   display: flex;
   align-items: center;
   justify-content: center;
   &:hover { background: rgba(148, 163, 184, 0.12); color: #fff; }
`;

const Status = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 14px;
   font-size: 12px;
   color: #bae6fd;
   background: rgba(14, 165, 233, 0.12);
   border-bottom: 1px solid rgba(14, 165, 233, 0.18);
`;

const Stage = styled.div`
   position: relative;
   background: #020617;
   aspect-ratio: 1280 / 800;
   max-height: calc(100vh - 220px);
`;

const Screen = styled.img`
   display: block;
   width: 100%;
   height: 100%;
   object-fit: contain;
   background: #020617;
`;

const Marker = styled.button<{ $active?: boolean }>`
   position: absolute;
   transform: translate(-50%, -50%);
   min-width: 18px;
   height: 18px;
   padding: 0 4px;
   border: 0;
   border-radius: 999px;
   font-size: 10px;
   font-weight: 700;
   cursor: default;
   color: #fff;
   z-index: 2;
   background: ${p => p.$active ? "#ef4444" : "rgba(14, 165, 233, 0.88)"};
   box-shadow: 0 0 0 2px ${p => p.$active ? "rgba(239, 68, 68, 0.35)" : "rgba(14, 165, 233, 0.25)"};
`;

const ClickRipple = styled.div`
   position: absolute;
   width: 42px;
   height: 42px;
   border-radius: 50%;
   border: 2px solid #f43f5e;
   pointer-events: none;
   animation: ${pulse} 0.9s ease-out infinite;
   z-index: 3;
`;

const Empty = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 10px;
   min-height: 220px;
   padding: 24px;
   font-size: 14px;
   color: #94a3b8;
   text-align: center;
`;

const Reopen = styled.button`
   position: fixed;
   right: 16px;
   top: 72px;
   z-index: 5000;
   display: flex;
   align-items: center;
   gap: 8px;
   border: 0;
   cursor: pointer;
   background: #0f172a;
   color: #e2e8f0;
   border-radius: 999px;
   padding: 10px 14px;
   box-shadow: 0 12px 30px rgba(2, 6, 23, 0.35);
   border: 1px solid rgba(56, 189, 248, 0.4);
   font-size: 13px;
   font-weight: 600;
`;

function applyFrame(
   payload: BrowserUsePreviewFrame,
   projectId: string | null | undefined,
   setFrame: React.Dispatch<React.SetStateAction<BrowserUsePreviewFrame | null>>,
   setOpen: React.Dispatch<React.SetStateAction<boolean>>,
   setMinimized: React.Dispatch<React.SetStateAction<boolean>>,
) {
   if (projectId && payload?.projectId && payload.projectId !== projectId) return;
   if (payload?.done) {
      setFrame((prev) => prev ? { ...prev, done: true, waiting: false, operation: "done" } : { ...payload, done: true });
      return;
   }
   setFrame((prev) => ({ ...(prev || {}), ...payload }));
   setOpen(true);
   setMinimized(false);
}

export function BrowserUsePreview({
   projectId,
   isRunning,
   enabled,
}: {
   projectId?: string | null;
   isRunning?: boolean;
   enabled?: boolean;
}) {
   const [frame, setFrame] = React.useState<BrowserUsePreviewFrame | null>(null);
   const [open, setOpen] = React.useState(false);
   const [minimized, setMinimized] = React.useState(false);

   React.useEffect(() => {
      const onPreview = (payload: BrowserUsePreviewFrame) => {
         applyFrame(payload, projectId, setFrame, setOpen, setMinimized);
      };
      const onCustom = (event: Event) => {
         const detail = (event as CustomEvent<BrowserUsePreviewFrame>).detail;
         if (detail) onPreview(detail);
      };
      socket.on("browserUsePreview", onPreview);
      window.addEventListener(BROWSER_USE_PREVIEW_EVENT, onCustom);
      return () => {
         socket.off("browserUsePreview", onPreview);
         window.removeEventListener(BROWSER_USE_PREVIEW_EVENT, onCustom);
      };
   }, [projectId]);

   React.useEffect(() => {
      if (isRunning && enabled) {
         setOpen(true);
         setMinimized(false);
         setFrame((prev) => prev?.image ? prev : {
            projectId: projectId || undefined,
            waiting: true,
            operation: "starting",
            done: false,
         });
      }
   }, [isRunning, enabled, projectId]);

   if (!open) {
      if ((isRunning && enabled) || frame) {
         return (
            <Reopen type="button" onClick={() => setOpen(true)}>
               <Monitor size={16} />
               Agent browser
            </Reopen>
         );
      }
      return null;
   }

   const viewport = frame?.viewport || { width: 1280, height: 800 };
   const actionKey = frame?.done ? "done" : (frame?.operation || "starting");
   const actionLabel = ACTION_COPY[actionKey] || actionKey;
   const highlight = frame?.elements?.find((item) => item.index === frame?.highlightIndex);
   const statusText = frame?.label || highlight?.text
      ? `${actionLabel}${frame?.label || highlight?.text ? ` “${frame?.label || highlight?.text}”` : ""}`
      : actionLabel;

   return (
      <Panel $minimized={minimized}>
         <Chrome>
            <Dots aria-hidden="true"><span /><span /><span /></Dots>
            <Title><Monitor size={14} /> Agent browser</Title>
            <ActionChip>{actionKey}{frame?.step ? ` · ${frame.step}` : ""}</ActionChip>
            <UrlBar title={frame?.url}>
               <Lock size={12} />
               <UrlText>{frame?.url || frame?.title || "Waiting for the agent to open a page"}</UrlText>
            </UrlBar>
            <IconBtn type="button" onClick={() => setMinimized((value) => !value)} aria-label="Minimize preview">
               <Minus size={16} />
            </IconBtn>
            <IconBtn type="button" onClick={() => setOpen(false)} aria-label="Close preview">
               <X size={16} />
            </IconBtn>
         </Chrome>
         {minimized ? (
            <Empty>Live browser is running on the canvas. Expand to watch the agent.</Empty>
         ) : (
            <>
               <Status>
                  <MousePointerClick size={14} />
                  {statusText}
               </Status>
               {frame?.image ? (
                  <Stage>
                     <Screen src={`data:image/jpeg;base64,${frame.image}`} alt={frame.title || "Agent browser"} />
                     {(frame.elements || []).map((item) => {
                        if (!item.box) return null;
                        const left = `${((item.box.x + item.box.width / 2) / viewport.width) * 100}%`;
                        const top = `${((item.box.y + item.box.height / 2) / viewport.height) * 100}%`;
                        return (
                           <React.Fragment key={item.index}>
                              {item.index === frame.highlightIndex ? (
                                 <ClickRipple style={{ left, top }} />
                              ) : null}
                              <Marker
                                 type="button"
                                 $active={item.index === frame.highlightIndex}
                                 style={{ left, top }}
                                 title={item.text || `${item.tag || "el"} ${item.index}`}
                              >
                                 {item.index}
                              </Marker>
                           </React.Fragment>
                        );
                     })}
                  </Stage>
               ) : (
                  <Empty>
                     <Monitor size={28} />
                     Watch this panel while the agent opens the page, clicks, and reads text.
                  </Empty>
               )}
            </>
         )}
      </Panel>
   );
}
